import { FunctionComponent } from "react";

import { InputField } from "@/components/InputField/InputField";
import { MAX_LOOKALIKE_DOMAINS } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";
import { normalizeDomainInput } from "@/pages/Private/pages/CompanyEnrich/utils/normalizeDomainInput";

import { LookalikeConfig } from "../onboardingListTypes";
import { ConfigField, ConfigGrid, useConfigTranslation, useFieldError } from "./ConfigField";
import { ConfigFormProps } from "./configFormProps";
import { LookupMultiField, OptionMultiField, SuggestingTagField } from "./FilterFields";

/** COMPANY_ENRICH / LOOK_A_LIKE — seed domains, then narrow the lookalikes down. */
export const LookalikeForm: FunctionComponent<ConfigFormProps<LookalikeConfig>> = ({
	value,
	errors,
	options,
	onChange,
}) => {
	const ts = useConfigTranslation();
	const errorFor = useFieldError(errors);

	return (
		<ConfigGrid>
			<SuggestingTagField
				full
				required
				error={errorFor("similarTo")}
				fetchFn={options.fetchCompanyDomains}
				hint={`${value.domains.length}/${MAX_LOOKALIKE_DOMAINS}`}
				label={ts("similarTo")}
				normalizeValue={normalizeDomainInput}
				placeholder={ts("similarToPlaceholder")}
				testId="onboarding-lookalike-domains"
				values={value.domains}
				onChange={(domains) => onChange({ ...value, domains })}
			/>

			<LookupMultiField
				fetchFn={options.fetchCompanyCountries}
				label={ts("companyHq")}
				placeholder={ts("companyHqPlaceholder")}
				testId="onboarding-lookalike-companyCountries"
				titleOf={options.countryTitleOf}
				values={value.companyCountries}
				onChange={(companyCountries) => onChange({ ...value, companyCountries })}
			/>

			<OptionMultiField
				label={ts("employees")}
				options={options.employeeOptions}
				placeholder={ts("employeesPlaceholder")}
				values={value.employees}
				onChange={(employees) => onChange({ ...value, employees })}
			/>

			<SuggestingTagField
				fetchFn={options.fetchPositions}
				label={ts("findTitles")}
				placeholder={ts("findTitlesPlaceholder")}
				testId="onboarding-lookalike-positions"
				values={value.positionQuery}
				onChange={(positionQuery) => onChange({ ...value, positionQuery })}
			/>

			<OptionMultiField
				label={ts("seniority")}
				options={options.seniorityOptions}
				placeholder={ts("seniorityPlaceholder")}
				values={value.seniority}
				onChange={(seniority) => onChange({ ...value, seniority })}
			/>

			<LookupMultiField
				fetchFn={options.fetchCountries}
				label={ts("personLocation")}
				placeholder={ts("personLocationPlaceholder")}
				testId="onboarding-lookalike-countries"
				titleOf={options.countryTitleOf}
				values={value.countries}
				onChange={(countries) => onChange({ ...value, countries })}
			/>

			<ConfigField
				full
				error={errorFor("semanticQuery")}
				hint={ts("semanticQueryHint")}
				label={ts("semanticQuery")}
			>
				<InputField
					error={Boolean(errors.semanticQuery)}
					handleChange={(event) => onChange({ ...value, semanticQuery: event.target.value })}
					name="lookalikeSemanticQuery"
					placeholder={ts("semanticQueryPlaceholder")}
					showError={Boolean(errors.semanticQuery)}
					value={value.semanticQuery}
				/>
			</ConfigField>
		</ConfigGrid>
	);
};
